import { NAVIGATION, type NavItem } from "./navigation";
import { WORKSPACES, type Workspace, type WorkspaceId } from "./workspace-list";
import { PLATFORM_WORKSPACES } from "./workspace-list-platform";

export { WORKSPACES, type Workspace, type WorkspaceId } from "./workspace-list";
export { PLATFORM_WORKSPACES } from "./workspace-list-platform";

/** A building's workspaces, or the console's for a platform member outside any organization.
 * Defaults to a building's. */
export type WorkspaceContext = "tenant" | "platform";

const LISTS: Readonly<Record<WorkspaceContext, readonly Workspace[]>> = {
  tenant: WORKSPACES,
  platform: PLATFORM_WORKSPACES,
};

/* Settings is in both lists; by id it is the building's. */
export const WORKSPACE_BY_ID: Readonly<Record<WorkspaceId, Workspace>> = Object.fromEntries(
  [...PLATFORM_WORKSPACES, ...WORKSPACES].map((workspace) => [workspace.id, workspace]),
) as Record<WorkspaceId, Workspace>;

/** Core, the one every unclaimed path belongs to. */
export const DEFAULT_WORKSPACE: Workspace = WORKSPACE_BY_ID.core;

/* Where an unclaimed path belongs: Core in a building, Tenant in the console. */
const FALLBACK: Readonly<Record<WorkspaceContext, Workspace>> = {
  tenant: DEFAULT_WORKSPACE,
  platform: WORKSPACE_BY_ID.tenant,
};

/** Longest prefix wins, because the settings hub holds screens whose feature belongs to another
 * group (`/settings/lead-fields` is CRM's editor but, to the viewer, in Settings). */
export function workspaceOfPath(pathname: string, context: WorkspaceContext = "tenant"): Workspace {
  let best = FALLBACK[context];
  let longest = 0;
  for (const workspace of LISTS[context]) {
    for (const prefix of workspace.owns) {
      const matches = pathname === prefix || pathname.startsWith(`${prefix}/`);
      if (matches && prefix.length > longest) {
        best = workspace;
        longest = prefix.length;
      }
    }
  }
  return best;
}

/** Pages that exist only as a workspace's own front door; Core covers every other address. */
const ONLY_IN_THEIR_WORKSPACE = ["/you", "/aria", "/ws"] as const;

const under = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/* A screen the settings hub lists stays in Settings wherever it lives, like Phone at
 * /sarah/numbers. */
const SETTINGS_PATHS = NAVIGATION.filter(
  (entry) => entry.surface === "settings" && inSettings(entry),
).map((entry) => entry.path);

/** Their pages open in their own workspace rather than Core. */
const OUT_OF_CORE: ReadonlySet<WorkspaceId> = new Set([
  "comms",
  "sarah",
  "shelby",
  "olivia",
  "michael",
  "grace",
  "nancy",
  "amy",
  "david",
]);

/** A screen is its workspace's wherever it is filed (Hiring's templates and the kiosk's settings
 * sit in the settings hub), so a click inside a workspace never throws the person into Core. */
function hasScreenAt(workspace: Workspace, pathname: string): boolean {
  /* The deepest screen only: the console's Organizations at /platform must not claim
   * /platform/settings. */
  const screen = NAVIGATION.filter((entry) => entry.path !== "/" && under(pathname, entry.path))
    // oxlint-disable-next-line unicorn/no-array-sort -- a fresh array; the phone app's Hermes has no toSorted
    .sort((a, b) => b.path.length - a.path.length)
    .at(0);
  return screen !== undefined && workspaceOfItem(screen).id === workspace.id;
}

function workspaceCovers(
  workspace: Workspace,
  pathname: string,
  context: WorkspaceContext,
): boolean {
  if (workspace.id === "settings" && SETTINGS_PATHS.some((path) => under(pathname, path))) {
    return true;
  }
  if (workspace.id === "core") {
    /* Settings' own addresses too: every door into them (Core's footer, a setup step, the Control
     * Center) opens the one Settings frame. */
    const owner = workspaceOfPath(pathname).id;
    if (OUT_OF_CORE.has(owner) || owner === "settings") return false;
    return !ONLY_IN_THEIR_WORKSPACE.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
  }
  const owner = workspaceOfPath(pathname, context);
  if (owner.id === workspace.id) return true;
  return (owner.id === "core" || owner.id === "settings") && hasScreenAt(workspace, pathname);
}

/** Chosen workspace while it covers the path, then Core, then the owner of the address for the two
 * pages only a workspace has. `workspaceOfPath` says who OWNS an address, this says where you ARE;
 * Front Desk Calls opened from Core stays in Core. */
export function workspaceFor(
  pathname: string,
  chosen?: WorkspaceId | null,
  context: WorkspaceContext = "tenant",
): Workspace {
  /* A choice made in the other context (before entering a tenant, say) does not carry over. */
  const preferred = LISTS[context].find((workspace) => workspace.id === chosen);
  if (preferred && workspaceCovers(preferred, pathname, context)) return preferred;
  const fallback = FALLBACK[context];
  if (workspaceCovers(fallback, pathname, context)) return fallback;
  return workspaceOfPath(pathname, context);
}

/** Its claim, else its group's. A platform-only screen is the console's unless `context` says (the
 * role editor). */
export function workspaceOfItem(
  entry: Pick<NavItem, "id" | "group" | "platformOnly">,
  context: WorkspaceContext = entry.platformOnly ? "platform" : "tenant",
): Workspace {
  const list = LISTS[context];
  const claimed = list.find((workspace) => workspace.items?.includes(entry.id));
  if (claimed) return claimed;
  return list.find((workspace) => workspace.groups.includes(entry.group)) ?? FALLBACK[context];
}

/* The console's Settings holds only what is filed there: Luna's lines and Tenant's plans stay
 * theirs. */
function inSettings(entry: NavItem): boolean {
  return !entry.platformOnly || workspaceOfItem(entry).id === "settings";
}

/** Every screen this workspace holds, rail or hub. Settings reaches across groups (every screen the
 * hub lists), and the personal settings You claims stay You's while Settings lists them too, since
 * "my profile" is what people look for there. */
export function workspaceScreens(workspace: Workspace, items: readonly NavItem[]): NavItem[] {
  if (workspace.id === "core") return [...items];
  if (workspace.id === "settings") {
    return items.filter((entry) => {
      const owner = workspaceOfItem(entry).id;
      if (owner === "you") return entry.surface === "settings";
      return owner === "settings" || (entry.surface === "settings" && inSettings(entry));
    });
  }
  return items.filter((entry) => workspaceOfItem(entry).id === workspace.id);
}

/** The rows this workspace's rail lists: a group-owned screen only if it lives in the rail, a
 * claimed screen wherever it lived before (claiming it is the decision to put it here). */
export function workspaceNavigation(workspace: Workspace, items: readonly NavItem[]): NavItem[] {
  /* Core lists every rail screen but the ones that moved to their own workspace. */
  if (workspace.id === "core") {
    return items.filter(
      (entry) =>
        (entry.surface ?? "sidebar") === "sidebar" && !OUT_OF_CORE.has(workspaceOfItem(entry).id),
    );
  }
  const claims = new Set<string>(workspace.items ?? []);
  return items.filter((entry) => {
    if (workspaceOfItem(entry).id !== workspace.id) return false;
    if (claims.has(entry.id)) return true;
    return (entry.surface ?? "sidebar") === "sidebar";
  });
}

/** A workspace with no visible screen is not offered. Aria has no screens, so the caller passes its
 * entitlement. Deliberately NOT a separate permission: access follows from reachable screens, so
 * paid tiers gating screens stay truthful. */
export function visibleWorkspaces(
  items: readonly NavItem[],
  options: { aria: boolean; you?: boolean; context?: WorkspaceContext } = {
    aria: false,
  },
): Workspace[] {
  return LISTS[options.context ?? "tenant"].filter((workspace) => {
    if (workspace.id === "aria") return options.aria;
    // Everybody signed in to an organization has a profile, so everybody has a You.
    if (workspace.id === "you") return options.you ?? false;
    /* Judged by what it owns, not by its rail rows: Settings keeps most things in the hub. Your
     * own settings do not count, since everybody has them and You lists them. */
    return workspaceScreens(workspace, items).some((entry) => entry.scope !== "personal");
  });
}

/** Its front door if reachable, otherwise the first of its screens they can reach: a front door
 * somebody may not enter redirects to an error, and the switcher would have sent them. */
export function landingFor(workspace: Workspace, items: readonly NavItem[]): string {
  /* Its front door is a screen built for it, so there is no refusal to route around. Unless the
   * door is itself a registry screen: Nancy's orders are, and a kitchen without her still has
   * Menus. */
  const doorIsAScreen = NAVIGATION.some((entry) => entry.path === workspace.home);
  const screens = workspaceScreens(workspace, items);
  if (
    !doorIsAScreen &&
    ONLY_IN_THEIR_WORKSPACE.some((prefix) => workspace.home.startsWith(prefix))
  ) {
    return workspace.home;
  }
  if (screens.length === 0 || screens.some((entry) => entry.path === workspace.home)) {
    return workspace.home;
  }
  return screens[0]?.path ?? workspace.home;
}
